/**
 * RetireItemsDialog — the one user-facing way to take items out of play.
 *
 * Owner ruling (2026-09-22): "Delete item" is no longer a permanent delete for
 * users. It is **Retire**: the item leaves every list (exit_status → archived)
 * but its record and history stay, and it can be taken back while nothing
 * depends on it. Every retire goes through the governed `retire_new_item` RPC
 * (mig 746) via `itemsStore.retireItems`, with one written reason.
 *
 * When something already uses an item (a pool, session, plan, decision or
 * active relationship) the server refuses and names it. For those, this dialog
 * shows the refusal and offers the normal archive path — Exit status →
 * Archived, the same move as Classification → Exit status and the list's
 * "Archive" row action (canon 04: archived = removed from active
 * consideration, retained, restorable). No new lifecycle is invented here.
 *
 * Built on the shared ds `Modal` (not a hand-rolled overlay).
 */
import { useEffect, useState } from 'react';
import { Modal, TextArea, Callout } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { useItemsStore, type RetireItemFailure } from '@/stores/prioritization/itemsStore';
import { describeRetireFailures } from '@/lib/itemRetireFeedback';

export interface RetireItemsDialogProps {
  open: boolean;
  onClose: () => void;
  /** The items to retire. One item reads as a single retire. */
  items: Array<{ id: string; title?: string | null }>;
  /** Called with the ids that were actually retired (may be a subset). */
  onRetired?: (retiredIds: string[]) => void;
  /** Called with the ids archived through the in-use fallback. */
  onArchived?: (archivedIds: string[]) => void;
  /** Called once per attempt with the full outcome (e.g. "Undo this import"). */
  onFinished?: (outcome: { retired: string[]; failed: RetireItemFailure[] }) => void;
  /**
   * Runs after the reason is given and before any item is retired — e.g. an
   * import undo dissolves the relationships it created, so they don't block
   * the retire. Errors are swallowed per the caller's own best-effort rules.
   */
  beforeRetire?: () => Promise<void>;
  /** Overrides for a caller-specific framing ("Undo this import"). */
  title?: string;
  explain?: React.ReactNode;
  confirmLabel?: string;
}

export function RetireItemsDialog({
  open,
  onClose,
  items,
  onRetired,
  onArchived,
  onFinished,
  beforeRetire,
  title,
  explain,
  confirmLabel,
}: RetireItemsDialogProps) {
  const retireItems = useItemsStore((s) => s.retireItems);
  const updateItem = useItemsStore((s) => s.updateItem);
  const { showToast } = useToast();

  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [inUse, setInUse] = useState<RetireItemFailure[]>([]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setReason('');
      setSummary(null);
      setInUse([]);
      setDone(false);
    }
  }, [open]);

  const count = items.length;
  const single = count === 1;
  const name = single ? (items[0].title?.trim() || 'this item') : `${count} items`;
  const trimmed = reason.trim();

  const handleRetire = async () => {
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      if (beforeRetire) {
        try { await beforeRetire(); } catch { /* best-effort; the retire still reports honestly */ }
      }
      const { retired, failed } = await retireItems(items.map((i) => i.id), trimmed);
      if (retired.length > 0) onRetired?.(retired);
      onFinished?.({ retired, failed });
      if (failed.length === 0) {
        showToast(
          'success',
          single ? 'Item retired' : 'Items retired',
          single
            ? `“${name}” left every list. Its record and history stay.`
            : `Retired ${retired.length} of ${count}. Their records and history stay.`,
        );
        onClose();
        return;
      }
      setDone(true);
      if (single) {
        const f = failed[0];
        setSummary(`Can't retire “${name}”: ${f.reason}${f.remedy ? ` — ${f.remedy}` : ''}`);
      } else {
        setSummary(describeRetireFailures(retired.length, count, failed));
      }
      setInUse(failed.filter((f) => !!f.blockedBy));
    } catch (err) {
      setSummary(err instanceof Error ? err.message : 'The items could not be retired.');
    } finally {
      setBusy(false);
    }
  };

  const handleArchiveInUse = () => {
    const ids = inUse.map((f) => f.id);
    for (const id of ids) {
      updateItem(id, { exit_status: 'archived' } as Parameters<typeof updateItem>[1]);
    }
    showToast(
      'success',
      ids.length === 1 ? 'Item archived' : 'Items archived',
      `${ids.length} item${ids.length === 1 ? '' : 's'} in use moved to Archived. Change it back any time under Classification → Exit status.`,
    );
    onArchived?.(ids);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      title={title ?? (single ? 'Retire this item' : `Retire ${count} items`)}
      dismissible={!busy}
      size="sm"
      footer={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            {done ? 'Close' : 'Cancel'}
          </Button>
          {done && inUse.length > 0 && (
            <Button type="button" variant="primary" size="sm" onClick={handleArchiveInUse} disabled={busy}>
              {inUse.length === 1 ? 'Archive it instead' : `Archive the ${inUse.length} in use instead`}
            </Button>
          )}
          {!done && (
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={() => void handleRetire()}
              disabled={busy || !trimmed}
            >
              {busy ? 'Retiring…' : confirmLabel ?? (single ? 'Retire item' : `Retire ${count} items`)}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-[var(--text-sec)]">
          {explain ?? (
            <>
              {single ? `“${name}”` : 'These items'} will leave every list. The record and its history
              stay, and it can be taken back while nothing uses it. Items already in a pool, session,
              plan or decision can&apos;t be retired — you can archive those instead.
            </>
          )}
        </p>
        {!done && (
          <div>
            <label htmlFor="retire-reason" className="mb-1 block text-xs font-medium text-[var(--text-ter)]">
              Reason (required)
            </label>
            <TextArea
              id="retire-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this leaving the lists?"
              rows={3}
              disabled={busy}
              autoFocus
            />
          </div>
        )}
        {summary && <Callout tone="coral">{summary}</Callout>}
      </div>
    </Modal>
  );
}
